import { X } from "lucide-react";
import { useEffect, useState } from "react";

const SUCCESS_TOAST_MS = 5000;
const ACTION_TOAST_MS = 8000;
const MAX_TOASTS = 5;

export type ToastType = "success" | "error";

type ToastAction = { label: string; onAction: () => void };

type ToastItem = {
  id: string;
  type: ToastType;
  message: string;
  action?: ToastAction;
};

type ToastListener = (next: ToastItem[]) => void;

const listeners = new Set<ToastListener>();
let toasts: ToastItem[] = [];

function emit() {
  for (const listener of listeners) listener(toasts);
}

function pushToast(message: string, type: ToastType, action?: ToastAction) {
  const next: ToastItem = {
    id: crypto.randomUUID(),
    type,
    message,
    ...(action ? { action } : {}),
  };
  toasts = [...toasts, next].slice(-MAX_TOASTS);
  emit();
}

function dismissToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function showToast(message: string, type: ToastType = "success"): void {
  pushToast(message, type);
}

export function showSuccessToast(message: string): void {
  pushToast(message, "success");
}

export function showErrorToast(message: string): void {
  pushToast(message, "error");
}

export function showToastWithAction(
  message: string,
  action: ToastAction,
  type: ToastType = "success"
): void {
  pushToast(message, type, action);
}

function ToastCard({ toast }: { toast: ToastItem }) {
  useEffect(() => {
    if (toast.type !== "success") return;
    const ms = toast.action ? ACTION_TOAST_MS : SUCCESS_TOAST_MS;
    const t = window.setTimeout(() => dismissToast(toast.id), ms);
    return () => window.clearTimeout(t);
  }, [toast.id, toast.type, toast.action]);

  const isError = toast.type === "error";
  return (
    <div
      role={isError ? "alert" : "status"}
      className={[
        "flex max-w-sm items-start gap-3 rounded-2xl border px-4 py-3 text-sm font-medium shadow-lg",
        isError
          ? "border-danger/30 bg-danger text-white"
          : "border-success/30 border-l-4 border-l-success bg-white text-ink",
      ].join(" ")}
    >
      <p className="min-w-0 flex-1">{toast.message}</p>
      {toast.action ? (
        <button
          type="button"
          className={[
            "shrink-0 font-semibold underline focus-visible:outline-none focus-visible:ring-2",
            isError
              ? "text-white focus-visible:ring-white/40"
              : "text-brand-700 focus-visible:ring-brand-500/40",
          ].join(" ")}
          onClick={() => {
            toast.action?.onAction();
            dismissToast(toast.id);
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        className={[
          "shrink-0 rounded-lg p-0.5 focus-visible:outline-none focus-visible:ring-2",
          isError
            ? "text-white/80 hover:bg-white/10 hover:text-white focus-visible:ring-white/40"
            : "text-muted hover:bg-slate-100 hover:text-ink focus-visible:ring-brand-500/40",
        ].join(" ")}
        aria-label="Dismiss"
        onClick={() => dismissToast(toast.id)}
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>(toasts);

  useEffect(() => {
    listeners.add(setItems);
    setItems(toasts);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="fixed right-4 top-[calc(3.5rem+1rem)] z-[70] flex flex-col gap-2">
      {items.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
